import React from "react";
import { Link } from "react-router-dom";
import { type AuditLog } from "../types";
import {
  PlusCircleIcon,
  UserPlusIcon,
  LogOutIcon,
  XCircleIcon,
  Trash2Icon,
  Edit2Icon,
  UserCheckIcon,
  UserXIcon,
  GiftIcon,
  InfoIcon,
  SendIcon,
  StarIcon,
  HistoryIcon,
} from "./icons/FeatherIcons";
import { toJsDate } from "../utils/toJsDate";
import { useCachedImage } from "../hooks/useCachedImage";
import { fallbackImage } from "../utils/getCachedImage";

interface AuditLogItemProps {
  log: AuditLog;
}

const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
  if (e.currentTarget.src !== fallbackImage) {
    e.currentTarget.src = fallbackImage;
  }
};

const getIconForType = (type: AuditLog["type"]) => {
  switch (type) {
    case "ACTIVITY_CREATE":
      return { Icon: PlusCircleIcon, color: "bg-green-100 text-green-600" };
    case "ACTIVITY_JOIN":
      return { Icon: UserPlusIcon, color: "bg-blue-100 text-blue-600" };
    case "ACTIVITY_LEAVE":
      return { Icon: LogOutIcon, color: "bg-orange-100 text-orange-600" };
    case "ACTIVITY_PASS":
      return { Icon: XCircleIcon, color: "bg-gray-100 text-gray-500" };
    case "ACTIVITY_DELETE":
      return { Icon: Trash2Icon, color: "bg-red-100 text-red-600" };
    case "ACTIVITY_RESTORE":
      return { Icon: HistoryIcon, color: "bg-green-100 text-green-600" };
    case "ACTIVITY_UPDATE":
      return { Icon: Edit2Icon, color: "bg-indigo-100 text-indigo-600" };
    case "ACTIVITY_KICK_PARTICIPANT":
      return { Icon: UserXIcon, color: "bg-red-100 text-red-600" };
    case "ACTIVITY_KICKED":
      return { Icon: UserXIcon, color: "bg-red-100 text-red-600" };
    case "PROFILE_UPDATE":
      return { Icon: Edit2Icon, color: "bg-purple-100 text-purple-600" };
    case "FOLLOW_USER":
      return { Icon: UserCheckIcon, color: "bg-teal-100 text-teal-600" };
    case "UNFOLLOW_USER":
      return { Icon: UserXIcon, color: "bg-yellow-100 text-yellow-600" };
    case "GOT_FOLLOWER":
      return { Icon: GiftIcon, color: "bg-pink-100 text-pink-600" };
    case "FOLLOW_REQUEST_SENT":
      return { Icon: SendIcon, color: "bg-sky-100 text-sky-600" };
    case "FOLLOW_REQUEST_DECLINED":
      return { Icon: UserXIcon, color: "bg-red-100 text-red-600" };
    case "FOLLOW_REQUEST_CANCELLED":
      return { Icon: UserXIcon, color: "bg-gray-100 text-gray-500" };
    case "USER_JOINED_APP":
      return {
        Icon: StarIcon,
        color: "bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600",
      };
    default:
      return { Icon: InfoIcon, color: "bg-gray-100 text-gray-500" };
  }
};

const timeAgo = (dateInput: any) => {
  const date = toJsDate(dateInput);
  if (!date || isNaN(date.getTime())) return "N/A";
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + "h ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + "m ago";
  return Math.floor(seconds) + "s ago";
};

const AuditLogItem: React.FC<AuditLogItemProps> = ({ log }) => {
  const { Icon, color } = getIconForType(log.type);
  const cachedImage = log.fromUser
    ? useCachedImage(
        log.fromUser.profileImageUrl,
        log.fromUser.username,
        log.fromUser.id,
      )
    : undefined;

  return (
    <Link
      to={log.link}
      className="flex items-start p-3 rounded-lg hover:bg-gray-100/50 transition-colors group"
    >
      <div
        className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center mr-4 ${color}`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-gray-800 text-sm"
          dangerouslySetInnerHTML={{ __html: log.text }}
        ></p>
        <p className="text-xs text-gray-500 mt-1">{timeAgo(log.createdAt)}</p>
      </div>
      {log.fromUser && (
        <img
          src={cachedImage}
          alt={log.fromUser.username}
          onError={handleImageError}
          className="w-10 h-10 rounded-lg object-cover ml-4 flex-shrink-0"
        />
      )}
    </Link>
  );
};

export default AuditLogItem;
